/**
 * brand/favicon.ts — the tab icon, kept in step with the theme the viewer is actually looking at.
 *
 * `index.html` ships a static `/favicon.svg` carrying both liveries behind a `prefers-color-scheme` query,
 * so the tab is right before a single byte of JavaScript has run — including on a cold load, where the
 * bundle is still in flight. What that file cannot know is the viewer's *explicit* choice: it is a separate
 * document, so it can neither read `data-theme` on our `<html>` nor the custom properties it selects. Hence
 * this: once the bundle executes, the link is repointed at a `data:` URI built from the RESOLVED tokens.
 *
 * Resolving through `getComputedStyle` rather than branching on a theme string is deliberate. The cascade
 * has already answered the question — explicit choice beats operating system, and the stylesheet says how —
 * so reading the answer cannot disagree with the page the way a second copy of that precedence would.
 * `MARK_PALETTE` is the fallback for a renderer that does not resolve custom properties (jsdom, chiefly),
 * and `mark.test.ts` pins the two to the same values so the fallback can never be a different mark.
 *
 * Permitted by the Content-Security-Policy as it already stands: `img-src 'self' data:`.
 */
import { MARK_PALETTE, markSvg, type MarkPalette } from "./mark";

const TOKENS = {
  start: "--logo-start",
  end: "--logo-end",
  ink: "--logo-ink",
} as const;

const LINK_ID = "favicon-svg";

/** What the cascade would show right now, used only when custom properties do not resolve. */
function fallbackPalette(root: HTMLElement): MarkPalette {
  const chosen = root.dataset["theme"];
  if (chosen === "dark") return MARK_PALETTE.dark;
  if (chosen === "light") return MARK_PALETTE.light;
  const media =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
  return media !== null && media.matches ? MARK_PALETTE.dark : MARK_PALETTE.light;
}

function resolvePalette(): MarkPalette {
  const root = document.documentElement;
  const fallback = fallbackPalette(root);
  const computed = window.getComputedStyle(root);
  const read = (name: string, spare: string): string => {
    const value = computed.getPropertyValue(name).trim();
    return value === "" ? spare : value;
  };
  return {
    start: read(TOKENS.start, fallback.start),
    end: read(TOKENS.end, fallback.end),
    ink: read(TOKENS.ink, fallback.ink),
  };
}

/** `data:image/svg+xml,…`. Percent-encoded rather than base64: shorter, and legible in a devtools panel. */
export function faviconDataUri(palette: MarkPalette): string {
  return `data:image/svg+xml,${encodeURIComponent(markSvg(palette))}`;
}

function paint(): void {
  const href = faviconDataUri(resolvePalette());
  // Replaced rather than mutated: several browsers cache the icon against the element and ignore an href
  // that changes underneath them, which presents as a toggle that works once and then appears stuck.
  document.getElementById(LINK_ID)?.remove();
  const link = document.createElement("link");
  link.id = LINK_ID;
  link.rel = "icon";
  link.type = "image/svg+xml";
  link.href = href;
  document.head.append(link);
}

/**
 * Paints the tab icon and keeps it in step. Returns a disposer, so a test can install and tear down without
 * leaving listeners on a shared document.
 *
 * Two sources of change, and both are needed: the theme toggle writes `data-theme`, and the operating
 * system can change underneath a viewer who has never chosen. Watching only the first leaves an unchosen
 * viewer with yesterday's icon; watching only the second ignores the toggle entirely.
 */
export function installFavicon(): () => void {
  paint();

  const observer = new MutationObserver(paint);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

  const media =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
  media?.addEventListener("change", paint);

  return (): void => {
    observer.disconnect();
    media?.removeEventListener("change", paint);
  };
}
